import { CompareContext } from './result-compare.types';

export function formatValue(val: any): string {
  if (val === null || val === undefined) return '—';
  if (Array.isArray(val)) {
    return val.map(item => formatValue(item)).join(' | ');
  }
  if (typeof val === 'object') {
    return Object.entries(val)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? (v as any[]).join(', ') : v}`)
      .join(' · ');
  }
  return String(val);
}

export function formatConfidence(val: number | null | undefined): string {
  if (val === null || val === undefined) return '—';
  return (val * 100).toFixed(1) + '%';
}

export function getSimilarityColor(score: number | null): string {
  if (score === null) return 'inherit';
  if (score >= 0.9) return '#24c58c';
  if (score >= 0.7) return '#f0b429';
  return '#ff6b6b';
}

export function truncateMiddle(text: string, maxLength: number): string {
  if (!text || text.length <= maxLength) return text;
  const keep = Math.floor((maxLength - 3) / 2);
  return `${text.slice(0, keep)}...${text.slice(text.length - keep)}`;
}

export function getNonReferenceIndices(ctx: CompareContext): number[] {
  return ctx.dataList.map((_, i) => i).filter(i => ctx.resultIds[i] !== ctx.referenceResultId);
}

export function getArchiveAccuracy(ctx: CompareContext, archiveName: string, resultIndex: number): number | null {
  const data = ctx.dataList[resultIndex];
  if (!data?.field_similarities) return null;
  const prefix = `${archiveName}.`;
  const scores = Object.entries(data.field_similarities)
    .filter(([key]) => key.startsWith(prefix) && !key.includes('::'))
    .map(([, score]) => score);
  if (scores.length === 0) return null;
  return scores.reduce((a, b) => a + b, 0) / scores.length;
}

export function getQualityDistribution(ctx: CompareContext, resultIndex: number): { high: number; medium: number; low: number } {
  const counts = { high: 0, medium: 0, low: 0 };
  const data = ctx.dataList[resultIndex];
  if (!data?.field_similarities) return counts;
  Object.entries(data.field_similarities)
    .filter(([key]) => !key.includes('::'))
    .forEach(([, score]) => {
      if (score >= 0.9) counts.high++;
      else if (score >= 0.7) counts.medium++;
      else counts.low++;
    });
  return counts;
}

export function extractFieldName(ctx: CompareContext, key: string): string {
  const archiveName = ctx.allArchives.find(a => key.startsWith(`${a}.`));
  return archiveName ? key.slice(archiveName.length + 1) : key;
}

export function getMostProblematicFields(ctx: CompareContext, limit: number): { field: string; avgScore: number }[] {
  const fieldScores = new Map<string, number[]>();
  getNonReferenceIndices(ctx).forEach(idx => {
    const data = ctx.dataList[idx];
    if (!data.field_similarities) return;
    Object.entries(data.field_similarities)
      .filter(([key]) => !key.includes('::'))
      .forEach(([key, score]) => {
        const fieldName = extractFieldName(ctx, key);
        if (!fieldScores.has(fieldName)) fieldScores.set(fieldName, []);
        fieldScores.get(fieldName)!.push(score);
      });
  });
  const averaged = Array.from(fieldScores.entries()).map(([field, scores]) => ({
    field,
    avgScore: scores.reduce((a, b) => a + b, 0) / scores.length,
  }));
  return averaged.sort((a, b) => a.avgScore - b.avgScore).slice(0, limit);
}

export function getTopClass(archiveKvData: Record<string, any> | undefined): string | null {
  let top: string | null = null;
  let topScore = -1;
  Object.entries(archiveKvData || {}).forEach(([cls, val]) => {
    const score = parseFloat(val);
    if (!isNaN(score) && score > topScore) {
      topScore = score;
      top = cls;
    }
  });
  return top;
}

export function getClassAgreementRate(ctx: CompareContext, resultIndex: number): number | null {
  const refIndex = ctx.resultIds.indexOf(ctx.referenceResultId);
  const refData = ctx.dataList[refIndex];
  const data = ctx.dataList[resultIndex];
  if (!refData || !data) return null;

  let matches = 0;
  let total = 0;
  ctx.allArchives.forEach(archive => {
    const refTop = getTopClass(refData.archives[archive]?.kv_data);
    const top = getTopClass(data.archives[archive]?.kv_data);
    if (refTop !== null && top !== null) {
      total++;
      if (refTop === top) matches++;
    }
  });
  return total > 0 ? matches / total : null;
}

export function isClassificationMode(dataList: CompareContext['dataList']): boolean {
  return dataList.some(d => Object.values(d.archives).some(a => a.is_classification));
}
